import { useCallback } from 'react';
import { resolveScopedBindingFailureCount } from '../projectParameterOperationOutcome';
import { sanitizeRevitParameterNames } from '../../revitParameterNames';
import { fetchAvailableProjectParameters, summarizeParameterAvailability } from './availability';
import { rememberAvailableProjectParameters } from './availableProjectParameterCache';
import {
  collectProcessConditionParameters,
  collectProjectPropertyParameters,
} from './configuration';
import {
  buildProjectParameterCompletionNotification,
  buildScopedBindingCompletionNotification,
  notifyParameterAddition,
} from './feedback';
import { projectChangedResult, runCoalescedParameterAddition } from './operationIdentity';
import {
  type ParameterAdditionOptions,
  type ParameterAdditionResult,
  type ProcessProjectParameterScope,
} from './models';
import { type ParameterAdditionCore } from './useParameterAdditionCore';
import { type ParameterMutationActions } from './useParameterMutationActions';
import { collectConnectorBindingParameters } from './connectorPlan';
import {
  buildProcessProjectParameterBindingGroups,
  buildProcessProjectParameterMutationRequests,
  type ProcessProjectParameterRequest,
} from './processProjectParameterModePolicy';

export function useProcessProjectParameterAddition(
  core: ParameterAdditionCore,
  mutations: ParameterMutationActions,
) {
  const {
    addProcessAndProjectParametersInFlightRef,
    connectorConfig,
    currentProjectUndoKey,
    didProjectChange,
    menuConfig,
    processConfig,
    setIsAdding,
    showError,
    showInfo,
    showSuccess,
    showWarning,
    startParameterProgress,
  } = core;
  const { addAsProjectParameters } = mutations;

  const addProcessAndProjectParameters = useCallback(async (
    options: ParameterAdditionOptions = {},
    scope?: ProcessProjectParameterScope,
  ): Promise<ParameterAdditionResult> => {
    return runCoalescedParameterAddition(
      addProcessAndProjectParametersInFlightRef,
      async () => {
        const processConditionParams = sanitizeRevitParameterNames(
          scope?.processConditionParameterNames ??
            collectProcessConditionParameters(processConfig),
        );
        const projectPropertyParams = sanitizeRevitParameterNames(
          scope?.projectPropertyParameterNames ??
            collectProjectPropertyParameters(processConfig),
        );
        const allRequestedParams = sanitizeRevitParameterNames([
          ...processConditionParams,
          ...projectPropertyParams,
        ]);
        const connectorParameterNames = collectConnectorBindingParameters({
          menuConfig,
          connectorConfig,
        });
        const buildRequests = (
          parameterNames: string[],
          categories: string[],
        ): ProcessProjectParameterRequest[] =>
          buildProcessProjectParameterBindingGroups({
            parameterNames,
            connectorParameterNames,
            connectorBindingKind: connectorConfig.parameterBindingKind,
          }).map((group) => ({ ...group, categories }));
        const requests = [
          ...buildRequests(
            processConditionParams,
            ['Piping Systems'],
          ),
          ...buildRequests(
            projectPropertyParams,
            ['Project Information'],
          ),
        ];

        if (
          processConditionParams.length === 0 &&
          projectPropertyParams.length === 0
        ) {
          return { success: true, addedCount: 0 };
        }

        const requestedProjectKey = currentProjectUndoKey;
        if (!requestedProjectKey) {
          const error = 'Active Revit project identity is not available yet. Recheck this step before continuing.';
          notifyParameterAddition(options, showWarning, error);
          return { success: false, addedCount: 0, error };
        }

        setIsAdding(true);
        startParameterProgress({
          title: 'Adding process properties',
          message: 'Revit is adding process and project parameters',
          detail: 'Existing compatible parameters are skipped; missing bindings are created or extended.',
        });
        try {
          const availableBeforeDescriptors = await fetchAvailableProjectParameters({
            cacheScopeKey: requestedProjectKey,
            force: true,
          });
          const availableBefore = availableBeforeDescriptors.map((parameter) => parameter.name);
          if (didProjectChange(requestedProjectKey)) {
            const result = projectChangedResult();
            notifyParameterAddition(
              options,
              showWarning,
              result.error ?? 'The active Revit project changed.',
            );
            return result;
          }

          const results: ParameterAdditionResult[] = [];
          const mutationRequests = buildProcessProjectParameterMutationRequests(
            requests,
            availableBeforeDescriptors,
          );

          for (const request of mutationRequests) {
            const result = await addAsProjectParameters(
              request.parameterNames,
              {
                categories: request.categories,
                bindingKind: request.bindingKind,
                bindingConflictMode: 'treatAsExisting',
                parameterTypeHints: request.parameterTypeHints,
              },
            );
            results.push(result);
            if (didProjectChange(requestedProjectKey)) {
              const result = projectChangedResult();
              notifyParameterAddition(
                options,
                showWarning,
                result.error ?? 'The active Revit project changed.',
              );
              return result;
            }
            if (
              result.success &&
              (result.failedCount || 0) === 0 &&
              (result.conflictingCount || 0) === 0 &&
              (result.bindingConflictExistingCount || 0) === 0
            ) {
              rememberAvailableProjectParameters(
                request.parameterNames,
                request.bindingKind,
                requestedProjectKey,
                request.categories,
                request.parameterTypeHints,
              );
            }
          }

          if (results.length === 0) {
            const notification = buildProjectParameterCompletionNotification(
              summarizeParameterAvailability(
                allRequestedParams,
                availableBefore,
                availableBefore,
              ),
            );
            notifyParameterAddition(options, showInfo, notification.message);
            return {
              success: true,
              addedCount: 0,
              existingCount: allRequestedParams.length,
              queued: false,
              requestedCount: allRequestedParams.length,
            };
          }

          const failed = results.find((result) => !result.success);
          if (failed) {
            notifyParameterAddition(
              options,
              showWarning,
              failed.error ||
                'Some process or project parameters could not be queued.',
            );
            return failed;
          }

          notifyParameterAddition(
            options,
            showInfo,
            'Scoped parameter requests completed. Flow cache updated for this project.',
          );
          if (didProjectChange(requestedProjectKey)) {
            const result = projectChangedResult();
            notifyParameterAddition(
              options,
              showWarning,
              result.error ?? 'The active Revit project changed.',
            );
            return result;
          }

          const availableAfterDescriptors = await fetchAvailableProjectParameters({
            cacheScopeKey: requestedProjectKey,
            force: true,
          });
          const availableAfter = availableAfterDescriptors.map((parameter) => parameter.name);
          if (didProjectChange(requestedProjectKey)) {
            const result = projectChangedResult();
            notifyParameterAddition(
              options,
              showWarning,
              result.error ?? 'The active Revit project changed.',
            );
            return result;
          }
          const availabilitySummary = summarizeParameterAvailability(
            allRequestedParams,
            availableBefore,
            availableAfter,
          );
          const totalFailedCount = resolveScopedBindingFailureCount(
            results.reduce((sum, result) => sum + (result.failedCount || 0), 0),
            availabilitySummary.missingCount,
          );
          const totalConflictingCount = results.reduce(
            (sum, result) => sum + (result.conflictingCount || 0),
            0,
          );
          const scopedNotification = buildScopedBindingCompletionNotification({
            requestedCount: allRequestedParams.length,
            addedOrUpdatedCount: results.reduce(
              (sum, result) => sum + (result.addedCount || 0) + (result.updatedCount || 0),
              0,
            ),
            existingCount: results.reduce(
              (sum, result) => sum + (result.existingCount || 0),
              0,
            ),
            conflictingCount: totalConflictingCount,
            failedCount: totalFailedCount,
          });

          if (scopedNotification.severity === 'success') {
            notifyParameterAddition(
              options,
              showSuccess,
              scopedNotification.message,
            );
          } else if (scopedNotification.severity === 'warning') {
            notifyParameterAddition(
              options,
              showWarning,
              scopedNotification.message,
            );
          } else {
            notifyParameterAddition(
              options,
              showInfo,
              scopedNotification.message,
            );
          }

          return {
            success: totalFailedCount === 0 && totalConflictingCount === 0,
            addedCount: results.reduce(
              (sum, result) => sum + (result.addedCount || 0),
              0,
            ),
            updatedCount: results.reduce(
              (sum, result) => sum + (result.updatedCount || 0),
              0,
            ),
            existingCount: results.reduce(
              (sum, result) => sum + (result.existingCount || 0),
              0,
            ),
            failedCount: totalFailedCount,
            conflictingCount: totalConflictingCount,
            queued: results.some((result) => result.queued),
            requestedCount: allRequestedParams.length,
            failedParameters: results.flatMap((result) => result.failedParameters ?? []),
            conflictingParameters: results.flatMap((result) => result.conflictingParameters ?? []),
            failureMessages: results.flatMap((result) => result.failureMessages ?? []),
          };
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          notifyParameterAddition(
            options,
            showError,
            `Failed to queue process or project parameters: ${message}`,
          );
          return {
            success: false,
            addedCount: 0,
            error: message,
          };
        } finally {
          setIsAdding(false);
        }
      },
    );
  }, [
    addAsProjectParameters,
    addProcessAndProjectParametersInFlightRef,
    connectorConfig,
    currentProjectUndoKey,
    didProjectChange,
    menuConfig,
    processConfig,
    setIsAdding,
    showError,
    showInfo,
    showSuccess,
    showWarning,
    startParameterProgress,
  ]);

  return addProcessAndProjectParameters;
}
